import { Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzColorPickerModule } from 'ng-zorro-antd/color-picker';
import { BroadcastService } from '../broadcast.service';
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
} from 'rxjs';
import { Broadcast, BroadcastRequest, DrawerParams } from '../broadcast';
import { AuditFieldChange } from '../../audit-logs/audit-logs.interface';
import { LookupService } from '../../../core/services/lookup.service';
import { finalize, switchMap, take, tap } from 'rxjs/operators';
import { LookupItem } from '../../../core/models/lookup.models';
import { DrawerService } from '../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CommonService } from '../../../shared/services/common.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { TimeZoneService } from '../../../core/services/timezone.service';
import { AuditContextService } from '../../audit-logs/audit-context.service';
import { QuillEditorComponent } from 'ngx-quill';
import { QuillModules } from 'ngx-quill/config';

@Component({
  selector: 'app-broadcast-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzDatePickerModule,
    NzSwitchModule,
    NzColorPickerModule,
    NzSpinModule,
    QuillEditorComponent,
  ],
  templateUrl: './broadcast-form.component.html',
  styleUrl: './broadcast-form.component.scss',
})
export class BroadcastFormComponent implements OnInit, OnDestroy {
  drawerBroadcastParams: DrawerParams = { mode: 'CREATE' };

  broadcastForm!: FormGroup;
  loading$ = new BehaviorSubject<boolean>(false);
  broadcastTypes$ = new BehaviorSubject<LookupItem[]>([]);
  organizations$ = new BehaviorSubject<LookupItem[]>([]);
  organizationsLoading$ = new BehaviorSubject<boolean>(false);
  noOrganizationsFound = false;
  private readonly searchOrganization$ = new Subject<string>();

  private readonly destroy$ = new Subject<void>();

  readonly GENERAL_BROADCAST_TYPE_ID = 104;
  readonly IMPORTANT_BROADCAST_TYPE_ID = 105;
  readonly MAX_MESSAGE_LENGTH = 500;
  private readonly DEFAULT_BROADCAST_COLOR = '#1890ff';
  private readonly HEX_COLOR_PATTERN = /^#(?:[A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
  readonly messageEditorModules: QuillModules = {
    toolbar: [['bold', 'italic', 'underline'], ['link']],
  };
  readonly viewMessageEditorModules: QuillModules = {
    toolbar: false,
  };
  isGeneralBroadcastType = false;

  private broadcastTargetAudiences: {
    organizationId: number;
    organizationName?: string;
  }[] = [];
  broadCaseData: Broadcast | null = null; // Store original broadcast data for audit comparison

  constructor(
    private readonly fb: FormBuilder,
    private readonly broadcastService: BroadcastService,
    private readonly lookupService: LookupService,
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly timeZoneService: TimeZoneService,
    private readonly auditContextService: AuditContextService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.drawerService
      .getParams<DrawerParams>(DRAWER_IDS.BROADCAST)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerBroadcastParams = params;
        }
      });
    this.initForm();
    this.loadFormData();
    this.setupOrganizationSearch();
    this.setupBroadcastTypeChanges();

    if (
      this.drawerBroadcastParams.mode !== 'CREATE' &&
      this.drawerBroadcastParams.id
    ) {
      this.loadBroadcastData(this.drawerBroadcastParams.id);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isImportantUpdate(): boolean {
    return (
      this.broadcastForm?.get('broadcastTypeId')?.value ===
      this.IMPORTANT_BROADCAST_TYPE_ID
    );
  }

  initForm(): void {
    this.broadcastForm = this.fb.group(
      {
        broadcastTypeId: [null, Validators.required],
        title: [null, Validators.required],
        message: [
          null,
          [
            this.richTextRequiredValidator(),
            this.richTextMaxLengthValidator(this.MAX_MESSAGE_LENGTH),
          ],
        ],
        bannerColor: [
          this.DEFAULT_BROADCAST_COLOR,
          [Validators.pattern(this.HEX_COLOR_PATTERN)],
        ],
        isDraft: [true],
        startDateTime: [null],
        endDateTime: [null],
        targetOrganizations: [[], Validators.required],
      },
      {
        validators: this.validateDateRange(),
      }
    );

    this.updateDateFieldValidators();

    if (this.drawerBroadcastParams.mode === 'VIEW') {
      this.broadcastForm.disable();
    }
  }

  setupOrganizationSearch(): void {
    this.searchOrganization$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        tap(() => {
          this.organizationsLoading$.next(true);
          this.organizations$.next([]);
        }),
        switchMap(searchTerm =>
          this.lookupService.getLookupValue('Organization', searchTerm).pipe(
            finalize(() => {
              this.organizationsLoading$.next(false);
            })
          )
        )
      )
      .subscribe({
        next: response => {
          const selectedOrgIds =
            this.broadcastForm.get('targetOrganizations')?.value || [];
          const orgMap = new Map<number, LookupItem>();

          (response || []).forEach(org => orgMap.set(org.id, org));
          this.noOrganizationsFound = response.length === 0;

          this.broadcastTargetAudiences.forEach(audience => {
            if (
              !orgMap.has(audience.organizationId) &&
              audience.organizationName
            ) {
              orgMap.set(audience.organizationId, {
                id: audience.organizationId,
                value: audience.organizationName,
              });
            }
          });

          if (selectedOrgIds.length > 0) {
            const currentOrgs = this.organizations$.value || [];
            currentOrgs.forEach(org => {
              if (selectedOrgIds.includes(org.id) && !orgMap.has(org.id)) {
                orgMap.set(org.id, org);
              }
            });
          }

          this.organizations$.next(Array.from(orgMap.values()));
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.organizationsLoading$.next(false);
          this.noOrganizationsFound = true;
          this.organizations$.next([]);
        },
      });
  }

  onOrganizationSearch(searchTerm: string): void {
    this.searchOrganization$.next(searchTerm);
  }

  loadFormData(): void {
    this.loading$.next(true);
    this.lookupService
      .getLookupValue('BroadcastNotificationType', '')
      .pipe(takeUntil(this.destroy$))
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: response => {
          if (response?.length) {
            this.broadcastTypes$.next(response);
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loading$.next(false);
        },
      });
    this.loadOrganizations('');
  }

  loadOrganizations(searchTerm: string): void {
    this.organizationsLoading$.next(true);
    this.organizations$.next([]);

    this.lookupService
      .getLookupValue('Organization', searchTerm)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.organizationsLoading$.next(false);
        })
      )
      .subscribe({
        next: response => {
          const selectedOrgIds =
            this.broadcastForm.get('targetOrganizations')?.value || [];
          const orgMap = new Map<number, LookupItem>();

          this.noOrganizationsFound = response.length === 0;
          (response || []).forEach(org => orgMap.set(org.id, org));

          this.broadcastTargetAudiences.forEach(audience => {
            if (
              !orgMap.has(audience.organizationId) &&
              audience.organizationName
            ) {
              orgMap.set(audience.organizationId, {
                id: audience.organizationId,
                value: audience.organizationName,
              });
            }
          });

          if (selectedOrgIds.length > 0) {
            const currentOrgs = this.organizations$.value || [];
            currentOrgs.forEach(org => {
              if (selectedOrgIds.includes(org.id) && !orgMap.has(org.id)) {
                orgMap.set(org.id, org);
              }
            });
          }

          this.organizations$.next(Array.from(orgMap.values()));
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.organizationsLoading$.next(false);
          this.noOrganizationsFound = true;
          this.organizations$.next([]);
        },
      });
  }

  loadBroadcastData(id: number): void {
    this.loading$.next(true);

    this.broadcastService
      .getBroadcastById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response?.result) {
            const broadcast = response.result;
            this.broadCaseData = broadcast; // Store original data for audit comparison
            this.broadcastTargetAudiences = (
              broadcast.broadcastTargetAudiences || []
            ).map(audience => ({
              organizationId: audience.organizationId,
              organizationName: audience.organizationName || '',
            }));

            const targetOrganizations =
              broadcast.broadcastTargetAudiences?.map(
                audience => audience.organizationId
              ) || [];

            const startDateTime = broadcast.startDateTime
              ? this.timeZoneService.convertToUserTimeZone(
                  broadcast.startDateTime
                )
              : null;
            const endDateTime = broadcast.endDateTime
              ? this.timeZoneService.convertToUserTimeZone(
                  broadcast.endDateTime
                )
              : null;

            this.broadcastForm.patchValue({
              broadcastTypeId: broadcast.broadcastTypeId,
              title: broadcast.title,
              message: this.normalizeMessageForEditor(broadcast.message),
              bannerColor: this.normalizeHexColor(broadcast.bannerColor),
              isDraft: broadcast.isDraft,
              startDateTime: startDateTime,
              endDateTime: endDateTime,
              targetOrganizations: targetOrganizations,
            });

            this.isGeneralBroadcastType =
              broadcast.broadcastTypeId === this.GENERAL_BROADCAST_TYPE_ID;
            this.updateDateFieldValidators();
            this.loadOrganizations('');
          }
          this.loading$.next(false);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loading$.next(false);
        },
      });
  }

  saveAsDraft() {
    this.submitForm(true);
  }
  submitForm(isDraft: boolean): void {
    this.broadcastForm.get('isDraft')?.setValue(isDraft);
    if (this.broadcastForm.invalid) {
      return;
    }

    this.loading$.next(true);
    const formValue = this.broadcastForm.value;
    const sanitizedMessage = this.sanitizeRichText(formValue.message);
    const broadcastTargetAudiences = formValue.targetOrganizations.map(
      (orgId: number) => ({
        organizationId: orgId,
      })
    );

    const localStartDateTime = this.isGeneralBroadcastType
      ? null
      : formValue.startDateTime;
    const localEndDateTime = this.isGeneralBroadcastType
      ? null
      : formValue.endDateTime;

    const userTimezone = this.timeZoneService.getUserTimeZone();
    let utcStartDateTime = null;
    let utcEndDateTime = null;

    if (localStartDateTime) {
      utcStartDateTime = this.timeZoneService.convertSpecificTimezoneToUTC(
        localStartDateTime,
        userTimezone
      );
    }

    if (localEndDateTime) {
      utcEndDateTime = this.timeZoneService.convertSpecificTimezoneToUTC(
        localEndDateTime,
        userTimezone
      );
    }

    const bannerColor = this.getBannerColorForRequest(formValue);
    const formValueForAudit = {
      ...formValue,
      message: sanitizedMessage,
      bannerColor: bannerColor,
    };

    const broadcastRequest: BroadcastRequest = {
      broadcastTypeId: formValue.broadcastTypeId,
      title: formValue.title,
      message: sanitizedMessage,
      bannerColor: bannerColor,
      isDraft: formValue.isDraft,
      startDateTime: utcStartDateTime ? utcStartDateTime.toISOString() : '',
      endDateTime: utcEndDateTime ? utcEndDateTime.toISOString() : '',
      broadcastTargetAudiences: broadcastTargetAudiences,
    };

    let broadcastTypes: LookupItem[] = [];
    let organizations: LookupItem[] = [];
    this.broadcastTypes$
      .pipe(take(1))
      .subscribe(types => (broadcastTypes = types));
    this.organizations$.pipe(take(1)).subscribe(orgs => (organizations = orgs));
    let original: Broadcast | null = null;
    if (this.drawerBroadcastParams.mode !== 'CREATE' && this.broadCaseData) {
      original = this.broadCaseData;
    }
    broadcastRequest._modifiedFields = this.buildModifiedFieldsForAudit(
      formValueForAudit,
      original,
      broadcastTypes,
      organizations
    );
    const request$ =
      this.drawerBroadcastParams.mode === 'CREATE'
        ? this.broadcastService.createBroadcast(broadcastRequest)
        : this.broadcastService.updateBroadcast(
            this.drawerBroadcastParams.id!,
            broadcastRequest
          );

    const successMessage =
      this.drawerBroadcastParams.mode === 'CREATE'
        ? 'Broadcast created successfully'
        : 'Broadcast updated successfully';

    request$
      .pipe(takeUntil(this.destroy$))
      .pipe(
        finalize(() => {
          this.loading$.next(false);
          this.drawerService.closeDrawer(DRAWER_IDS.BROADCAST);
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', [successMessage]);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loading$.next(false);
        },
      });
  }

  private buildModifiedFieldsForAudit(
    formValue: {
      broadcastTypeId: number;
      title: string;
      message: string;
      bannerColor: string | null;
      isDraft: boolean;
      startDateTime: Date | string | null;
      endDateTime: Date | string | null;
      targetOrganizations: number[];
    },
    original?: Broadcast | null,
    broadcastTypes: LookupItem[] = [],
    organizations: LookupItem[] = []
  ): AuditFieldChange[] {
    // Helper: get label from lookup
    const getLabel = (list: LookupItem[], id: number) => {
      const found = list.find(x => x.id === id);
      return found?.organizationName ?? found?.value ?? id;
    };
    // Helper: Yes/No for boolean
    const yesNo = (val: boolean) => (val ? 'Yes' : 'No');
    // Enrich values for audit
    const enrich = (key: string, value: unknown) => {
      switch (key) {
        case 'broadcastTypeId':
          return getLabel(broadcastTypes, Number(value));
        case 'targetOrganizations':
          return (Array.isArray(value) ? value : []).map((id: number) =>
            getLabel(organizations, id)
          );
        case 'isDraft':
          return yesNo(!!value);
        case 'startDateTime':
        case 'endDateTime':
          return value &&
            (typeof value === 'string' ||
              typeof value === 'number' ||
              value instanceof Date)
            ? new Date(value).toISOString()
            : '';
        default:
          return value;
      }
    };
    // Build audit objects
    const auditOld: Partial<Record<string, unknown>> = original
      ? {
          broadcastTypeId: enrich('broadcastTypeId', original.broadcastTypeId),
          title: original.title,
          message: original.message,
          bannerColor:
            original.broadcastTypeId === this.IMPORTANT_BROADCAST_TYPE_ID
              ? this.normalizeHexColor(original.bannerColor)
              : null,
          isDraft: enrich('isDraft', original.isDraft),
          startDateTime: enrich('startDateTime', original.startDateTime),
          endDateTime: enrich('endDateTime', original.endDateTime),
          targetOrganizations: enrich(
            'targetOrganizations',
            original.broadcastTargetAudiences?.map(
              (a: { organizationId: number; organizationName?: string }) =>
                a.organizationId
            ) ?? []
          ),
        }
      : {
          broadcastTypeId: '',
          title: '',
          message: '',
          bannerColor: null,
          isDraft: '',
          startDateTime: '',
          endDateTime: '',
          targetOrganizations: [],
        };
    const auditNew: Partial<Record<string, unknown>> = {
      broadcastTypeId: enrich('broadcastTypeId', formValue.broadcastTypeId),
      title: formValue.title,
      message: formValue.message,
      bannerColor: formValue.bannerColor,
      isDraft: enrich('isDraft', formValue.isDraft),
      startDateTime: enrich('startDateTime', formValue.startDateTime),
      endDateTime: enrich('endDateTime', formValue.endDateTime),
      targetOrganizations: enrich(
        'targetOrganizations',
        formValue.targetOrganizations
      ),
    };
    const changes = this.auditContextService.buildFieldChanges(
      auditOld,
      auditNew,
      {
        sensitiveFields: [],
        excludeFields: ['id'],
        labelMap: {
          broadcastTypeId: 'Broadcast Type',
          title: 'Title',
          message: 'Message',
          bannerColor: 'Banner Color',
          isDraft: 'Is Draft',
          startDateTime: 'Start Date/Time',
          endDateTime: 'End Date/Time',
          targetOrganizations: 'Schools',
        },
      }
    );
    // Filter out unchanged fields (deep equality for arrays/objects)
    const isEqual = (a: unknown, b: unknown): boolean => {
      if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((v, i) => v === b[i]);
      }
      return a === b;
    };
    return changes.filter(f => !isEqual(f.oldValue, f.newValue));
  }

  cancel(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.BROADCAST);
  }

  switchToEditMode(): void {
    this.drawerBroadcastParams.mode = 'EDIT';
    this.drawerService.updateDrawerTitle(
      DRAWER_IDS.BROADCAST,
      'Edit Broadcast'
    );
    this.broadcastForm.enable();
  }

  trackByFn(index: number, item: LookupItem): number {
    return item.id;
  }

  onBannerColorChange(event: {
    color: { toHexString: () => string };
    format: string;
  }): void {
    const normalizedHex = this.normalizeHexColor(event.color);
    const bannerColorControl = this.broadcastForm.get('bannerColor');

    if (!bannerColorControl) {
      return;
    }

    bannerColorControl.setValue(normalizedHex, { emitEvent: false });
    bannerColorControl.updateValueAndValidity({ emitEvent: false });
  }

  getMessageCharacterCount(): number {
    return this.broadcastService.getPlainTextLength(
      this.broadcastForm?.get('message')?.value
    );
  }

  private normalizeHexColor(value: unknown): string {
    let candidate = this.DEFAULT_BROADCAST_COLOR;

    if (typeof value === 'string') {
      candidate = value;
    } else if (
      typeof value === 'object' &&
      value !== null &&
      'toHexString' in value &&
      typeof value.toHexString === 'function'
    ) {
      candidate = value.toHexString();
    }

    return this.HEX_COLOR_PATTERN.test(candidate)
      ? candidate
      : this.DEFAULT_BROADCAST_COLOR;
  }

  private getBannerColorForRequest(formValue: {
    broadcastTypeId: number;
    bannerColor: unknown;
  }): string | null {
    if (formValue.broadcastTypeId !== this.IMPORTANT_BROADCAST_TYPE_ID) {
      return null;
    }

    return this.normalizeHexColor(formValue.bannerColor);
  }

  private richTextRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.broadcastService.getPlainTextLength(control.value) > 0
        ? null
        : { required: true };
    };
  }

  private richTextMaxLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const actualLength = this.broadcastService.getPlainTextLength(
        control.value
      );

      return actualLength > maxLength
        ? {
            maxlength: {
              requiredLength: maxLength,
              actualLength: actualLength,
            },
          }
        : null;
    };
  }

  private normalizeMessageForEditor(value: unknown): string {
    if (typeof value !== 'string' || value.trim().length === 0) {
      return '';
    }

    if (this.broadcastService.containsHtml(value)) {
      return this.sanitizeRichText(value);
    }

    return value
      .split(/\r?\n\r?\n/)
      .map(paragraph => `<p>${paragraph.replaceAll(/\r?\n/g, '<br>')}</p>`)
      .join('');
  }

  private sanitizeRichText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    return this.sanitizer.sanitize(SecurityContext.HTML, value) ?? '';
  }

  setupBroadcastTypeChanges(): void {
    this.broadcastForm
      .get('broadcastTypeId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const wasGeneral = this.isGeneralBroadcastType;
        this.isGeneralBroadcastType = value === this.GENERAL_BROADCAST_TYPE_ID;

        if (!wasGeneral && this.isGeneralBroadcastType) {
          this.broadcastForm.get('startDateTime')?.setValue(null);
          this.broadcastForm.get('endDateTime')?.setValue(null);
        }

        this.updateDateFieldValidators();
      });
  }

  updateDateFieldValidators(): void {
    const startDateControl = this.broadcastForm.get('startDateTime');
    const endDateControl = this.broadcastForm.get('endDateTime');

    if (this.isGeneralBroadcastType) {
      startDateControl?.clearValidators();
      endDateControl?.clearValidators();
    } else {
      startDateControl?.setValidators([
        Validators.required,
        this.futureDateValidator(),
      ]);
      endDateControl?.setValidators(Validators.required);
    }

    startDateControl?.updateValueAndValidity();
    endDateControl?.updateValueAndValidity();
  }

  futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const inputDate = new Date(control.value);
      const inputDateOnly = new Date(inputDate);
      inputDateOnly.setHours(0, 0, 0, 0);

      return inputDateOnly < currentDate
        ? { pastDate: { value: control.value } }
        : null;
    };
  }

  validateDateRange(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      if (this.isGeneralBroadcastType) {
        return null;
      }

      const startDateControl = formGroup.get('startDateTime');
      const endDateControl = formGroup.get('endDateTime');

      if (
        !startDateControl ||
        !endDateControl ||
        !startDateControl.value ||
        !endDateControl.value
      ) {
        return null;
      }

      const startDate = new Date(startDateControl.value);
      const endDate = new Date(endDateControl.value);

      return endDate <= startDate ? { endDateBeforeStart: true } : null;
    };
  }
}
